import { useEffect, useState, Fragment } from "react";
import api, { apiError, fmtUSD } from "../../lib/api";
import { toast } from "sonner";
import { DealForm } from "./DealForm";
import { AnnouncePanel } from "./AnnouncePanel";
import { CircleManager } from "./CircleManager";
import { DealNotesAdmin } from "./DealNotesAdmin";
import { useAuth } from "../../context/AuthContext";

const EMPTY = {
  company: "", sector: "", region: "US", stage: "Late stage / C+", round_label: "",
  last_round_valuation: "", announced: "", price_per_share: "", minimum_usd: "",
  admin_charge: "2 percent administration charge", carry: "20 percent carried interest",
  closes_at: "", issuer_confirmation: "Not received", status: "open", spv_id: "", finder: "", target_allocation_usd: "",
  closes_date: "",
  view: "", against: "", company_history: "", price_context: "", seller: "",
  path_to_liquidity: "", unknowns: "", private: false, audience: [], circle_id: "",
};

export default function AdminDeals({ initialEditId, onConsumeEdit }) {
  const [deals, setDeals] = useState([]);
  const [spvs, setSpvs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [attaching, setAttaching] = useState(null);
  const [att, setAtt] = useState({ title: "", file: null });
  const [f, setF] = useState(EMPTY);
  const [stats, setStats] = useState({});
  const [busy, setBusy] = useState(false);
  const [projFor, setProjFor] = useState(null);
  const [proj, setProj] = useState({ low: "", base: "", high: "", horizon: "3 to 5 years" });
  const [members, setMembers] = useState([]);
  const [circles, setCircles] = useState([]);
  const [announceFor, setAnnounceFor] = useState(null);
  const [announce, setAnnounce] = useState({ recipients: "all", user_ids: [], message: "", send_at: "", threshold: "" });
  const [responses, setResponses] = useState({});
  const [scheduled, setScheduled] = useState({});
  const [allocCap, setAllocCap] = useState("");
  const { user } = useAuth();
  const isOwner = !!user?.is_private_owner;

  const inviteToCircle = async (name, email) => {
    const { data } = await api.post("/admin/private/invite", { name, email });
    setMembers((ms) => (ms.some((m) => m.user_id === data.user_id) ? ms : [...ms, data]));
    toast.success(`${data.name || data.email} can now be added and was emailed a welcome.`);
    return data;
  };

  const load = () => api.get("/deals").then((r) => setDeals(r.data)).catch(() => {});
  const loadCircles = () => api.get("/admin/circles").then((r) => setCircles(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/spvs").then((r) => setSpvs(r.data)).catch(() => {});
    api.get("/admin/deal-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data.filter((m) => m.role === "member"))).catch(() => {});
    if (user?.is_private_owner) loadCircles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialEditId && deals.length) {
      const d = deals.find((x) => x.deal_id === initialEditId);
      if (d) { startEdit(d); onConsumeEdit?.(); }
    }
  }, [deals, initialEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const startEdit = (d) => {
    setEditing(d.deal_id);
    setF({
      ...d, spv_id: d.spv_id || "", price_per_share: String(d.price_per_share), minimum_usd: String(d.minimum_usd),
      target_allocation_usd: d.target_allocation_usd != null ? String(d.target_allocation_usd) : "",
      closes_date: d.closes_date || "",
      unknowns: d.unknowns.join("\n"),
      private: !!d.private, audience: d.audience || [], circle_id: d.circle_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      ...f,
      spv_id: f.spv_id || null,
      finder: f.finder || null,
      closes_date: f.closes_date || null,
      target_allocation_usd: f.target_allocation_usd ? Number(f.target_allocation_usd) : null,
      price_per_share: Number(f.price_per_share),
      minimum_usd: Number(f.minimum_usd),
      unknowns: f.unknowns.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    payload.circle_id = f.circle_id || null;
    delete payload.deal_id; delete payload.memo_code; delete payload.created_at;
    delete payload.my_commitment; delete payload.spv; delete payload.attachments;
    delete payload.circle; delete payload.interest_count;
    try {
      if (editing) await api.put(`/admin/deals/${editing}`, payload);
      else await api.post("/admin/deals", payload);
      toast.success(editing ? "Deal updated." : "Deal created.");
      setEditing(null);
      setF(EMPTY);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const uploadAttachment = async (dealId) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", att.file);
      fd.append("title", att.title);
      await api.post(`/admin/deals/${dealId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Material attached. Members see it below the memo.");
      setAttaching(null);
      setAtt({ title: "", file: null });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const closeDeal = async (d) => {
    try {
      const { data } = await api.post(`/admin/deals/${d.deal_id}/close`);
      toast.success(`${d.company} closed. ${data.notified} committed member${data.notified === 1 ? "" : "s"} notified that allocations are being finalized.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const startProj = (d) => {
    setProjFor(projFor === d.deal_id ? null : d.deal_id);
    const p = d.projection || {};
    setProj({ low: p.low ?? "", base: p.base ?? "", high: p.high ?? "", horizon: p.horizon || "3 to 5 years" });
  };

  const saveProjection = async (dealId) => {
    setBusy(true);
    try {
      await api.put(`/admin/deals/${dealId}/projection`, {
        low: Number(proj.low), base: Number(proj.base), high: Number(proj.high), horizon: proj.horizon || "3 to 5 years",
      });
      toast.success("Projection saved. Members can model this deal in the calculator.");
      setProjFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const loadResponses = (dealId) =>
    api.get(`/admin/deals/${dealId}/responses`).then((r) => setResponses((s) => ({ ...s, [dealId]: r.data }))).catch(() => {});

  const loadScheduled = (dealId) =>
    api.get(`/admin/deals/${dealId}/scheduled`).then((r) => setScheduled((s) => ({ ...s, [dealId]: r.data }))).catch(() => {});

  const startAnnounce = (d) => {
    const next = announceFor === d.deal_id ? null : d.deal_id;
    setAnnounceFor(next);
    setAnnounce({ recipients: "all", user_ids: [], message: "", send_at: "", threshold: d.interest_threshold || "" });
    if (next) { loadResponses(d.deal_id); loadScheduled(d.deal_id); }
  };

  const toggleRecipient = (uid) =>
    setAnnounce((a) => ({ ...a, user_ids: a.user_ids.includes(uid) ? a.user_ids.filter((x) => x !== uid) : [...a.user_ids, uid] }));

  const sendAnnounce = async (dealId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/deals/${dealId}/announce`, {
        recipients: announce.recipients,
        user_ids: announce.recipients === "selected" ? announce.user_ids : [],
        message: announce.message || null,
        send_at: announce.send_at ? new Date(announce.send_at).toISOString() : null,
      });
      if (data.scheduled) {
        toast.success(`Scheduled for ${new Date(data.send_at).toLocaleString()}.`);
        setAnnounce({ ...announce, send_at: "" });
        loadScheduled(dealId);
      } else {
        toast.success(`Announcement sent to ${data.sent} member${data.sent === 1 ? "" : "s"}.`);
        loadResponses(dealId);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cancelScheduled = async (scheduleId, dealId) => {
    try {
      await api.post(`/admin/scheduled-announcements/${scheduleId}/cancel`);
      toast.success("Scheduled announcement cancelled.");
      loadScheduled(dealId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remind = async (dealId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/deals/${dealId}/remind`);
      toast.success(`Reminder sent to ${data.reminded} member${data.reminded === 1 ? "" : "s"} who had not responded.`);
      loadResponses(dealId);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveThreshold = async (dealId) => {
    try {
      await api.put(`/admin/deals/${dealId}/threshold`, { threshold: announce.threshold ? Number(announce.threshold) : 0 });
      toast.success(announce.threshold ? `You will be alerted at ${announce.threshold} interested.` : "Interest alert turned off.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const allocateWaitlist = async (dealId, userId) => {
    try {
      const { data } = await api.post(`/admin/deals/${dealId}/allocate-waitlist`, { user_id: userId });
      toast.success(`Allocated ${fmtUSD(data.allocated)}. The member has been notified.`);
      loadResponses(dealId);
    } catch (e) { toast.error(apiError(e)); }
  };

  const allocateAll = async (dealId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/deals/${dealId}/allocate-all`, { cap_usd: allocCap ? Number(allocCap) : null });
      toast.success(`Allocated ${data.allocated_count} member${data.allocated_count === 1 ? "" : "s"}, ${fmtUSD(data.total_allocated)} total.`);
      loadResponses(dealId);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      {isOwner && (
        <CircleManager circles={circles} members={members} onInvite={inviteToCircle} reload={() => { loadCircles(); load(); }} />
      )}
      <DealForm
        editing={editing}
        f={f}
        set={set}
        setVal={(k, v) => setF((s) => ({ ...s, [k]: v }))}
        spvs={spvs}
        busy={busy}
        onSave={save}
        onCancel={() => { setEditing(null); setF(EMPTY); }}
        isOwner={isOwner}
        members={members}
        circles={circles}
        onInvite={inviteToCircle}
      />
      {editing && f.circle_id && <DealNotesAdmin dealId={editing} />}

      <table className="table-solera mt-12" data-testid="admin-deals-table">
        <thead><tr><th>Memo</th><th>Company</th><th>Vehicle</th><th className="num">Minimum</th><th className="num">Committed / Target</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {deals.map((d) => (
            <Fragment key={d.deal_id}>
              <tr>
                <td className="text-muted-s fig t-small">{d.memo_code}</td>
                <td className="text-bone">{d.company}{d.private && <span className="t-label !text-[#C69A4E] ml-2" data-testid={`deal-private-badge-${d.deal_id}`}>Private</span>}{d.circle && <span className="t-label !text-[#C69A4E] ml-2" data-testid={`deal-circle-badge-${d.deal_id}`}>{d.circle.name}</span>}</td>
                <td className="text-muted-s t-small">{d.spv?.name || "-"}</td>
                <td className="num text-bone">{fmtUSD(d.minimum_usd)}</td>
                <td className="num text-bone" data-testid={`deal-gauge-${d.deal_id}`}>
                  {fmtUSD(stats[d.deal_id]?.committed || 0)}
                  {d.target_allocation_usd ? ` of ${fmtUSD(d.target_allocation_usd)}` : ""}
                  <span className="block t-cap text-muted-s">
                    {stats[d.deal_id]?.count || 0} commitments, {fmtUSD(stats[d.deal_id]?.allocated || 0)} allocated
                  </span>
                </td>
                <td className="text-muted-s">{d.status}</td>
                <td className="text-right whitespace-nowrap space-x-4">
                  <button onClick={() => startEdit(d)} className="t-label hover:!text-[#E8E6E1]" data-testid={`edit-deal-${d.deal_id}`}>Edit</button>
                  <button onClick={() => { setAttaching(attaching === d.deal_id ? null : d.deal_id); setAtt({ title: "", file: null }); }}
                    className="t-label hover:!text-[#E8E6E1]" data-testid={`attach-deal-${d.deal_id}`}>Attach</button>
                  <button onClick={() => startProj(d)} className="t-label hover:!text-[#E8E6E1]" data-testid={`projection-deal-${d.deal_id}`}>Projection</button>
                  <button onClick={() => startAnnounce(d)} className="t-label hover:!text-[#E8E6E1]" data-testid={`announce-deal-${d.deal_id}`}>Announce</button>
                  {d.status === "open" && (
                    <button onClick={() => closeDeal(d)} className="t-label hover:!text-[#E8E6E1]" data-testid={`close-deal-${d.deal_id}`}>Close</button>
                  )}
                </td>
              </tr>
              {attaching === d.deal_id && (
                <tr>
                  <td colSpan={7}>
                    <div className="flex flex-wrap gap-3 items-center py-2" data-testid="deal-attachment-form">
                      <input className="input-solera max-w-xs" placeholder="Title, e.g. Company deck" value={att.title}
                        onChange={(e) => setAtt({ ...att, title: e.target.value })} data-testid="attachment-title-input" />
                      <input type="file" className="input-solera t-small max-w-xs" onChange={(e) => setAtt({ ...att, file: e.target.files[0] })} data-testid="attachment-file-input" />
                      <button className="btn-primary" disabled={busy || !att.title || !att.file}
                        onClick={() => uploadAttachment(d.deal_id)} data-testid="attachment-upload-button">
                        {busy ? "Uploading" : "Attach"}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {projFor === d.deal_id && (
                <tr>
                  <td colSpan={7}>
                    <div className="flex flex-wrap gap-3 items-end py-2" data-testid="deal-projection-form">
                      <div><span className="t-label">Conservative x</span><input className="input-solera fig max-w-[120px] mt-1" type="number" step="0.1" value={proj.low} onChange={(e) => setProj({ ...proj, low: e.target.value })} data-testid="proj-low-input" /></div>
                      <div><span className="t-label">Base x</span><input className="input-solera fig max-w-[120px] mt-1" type="number" step="0.1" value={proj.base} onChange={(e) => setProj({ ...proj, base: e.target.value })} data-testid="proj-base-input" /></div>
                      <div><span className="t-label">Optimistic x</span><input className="input-solera fig max-w-[120px] mt-1" type="number" step="0.1" value={proj.high} onChange={(e) => setProj({ ...proj, high: e.target.value })} data-testid="proj-high-input" /></div>
                      <div><span className="t-label">Horizon</span><input className="input-solera max-w-[200px] mt-1" value={proj.horizon} onChange={(e) => setProj({ ...proj, horizon: e.target.value })} data-testid="proj-horizon-input" /></div>
                      <button className="btn-primary" disabled={busy || !proj.low || !proj.base || !proj.high} onClick={() => saveProjection(d.deal_id)} data-testid="proj-save-button">Save projection</button>
                    </div>
                  </td>
                </tr>
              )}
              {announceFor === d.deal_id && (
                <tr>
                  <td colSpan={7}>
                    <AnnouncePanel
                      d={d}
                      members={members}
                      announce={announce}
                      setAnnounce={setAnnounce}
                      toggleRecipient={toggleRecipient}
                      scheduled={scheduled}
                      responses={responses}
                      busy={busy}
                      allocCap={allocCap}
                      setAllocCap={setAllocCap}
                      sendAnnounce={sendAnnounce}
                      cancelScheduled={cancelScheduled}
                      remind={remind}
                      saveThreshold={saveThreshold}
                      allocateWaitlist={allocateWaitlist}
                      allocateAll={allocateAll}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
